"use client";

import { Check } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import { cn } from "@/lib/utils";

import { useSequence } from "../use-sequence";
import { AppWindow, Typewriter } from "../visual-kit";

// =============================================================================

const TYPING_SPEED = 40;

/**
 * Step 2: a Notion-like page where the client's needs get written down and
 * checked one by one.
 */
export function NotesVisual({ active }: { active: boolean }) {
  const t = useTranslations("Home.process.visuals.notes");
  const items = t.raw("items") as string[];
  const durations = useMemo(
    () => [
      ...items.map((item) => (item.length / TYPING_SPEED) * 1000 + 650),
      2800,
    ],
    [items]
  );
  const step = useSequence(active, durations);

  return (
    <AppWindow>
      {/* Cover */}
      <div className="relative h-[30%] overflow-hidden bg-[linear-gradient(90deg,#2a0b3a_0%,#6b1d86_22%,#0b0b0d_40%,#a5245f_56%,#e2533d_76%,#ff9b3d_100%)]">
        <span
          aria-hidden
          className="absolute top-1/2 left-[36%] -translate-y-[42%] text-[14cqw] leading-none font-extrabold tracking-[-0.06em] text-white/70 mix-blend-overlay blur-[0.12cqw]"
        >
          {t("cover")}
        </span>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_50%,transparent_20%,rgb(0_0_0/0.35)_100%)]" />
      </div>

      {/* Page */}
      <div className="relative px-[31%] pt-[3.4cqw]">
        <span className="absolute top-[-3.4cqw] left-[30%] text-[4.6cqw] leading-none">
          🏠
        </span>
        <p className="text-[3cqw] leading-tight font-bold tracking-[-0.02em]">
          {t("title")}
        </p>
        <p className="mt-[0.6cqw] text-[1.2cqw] text-black/60">
          {t("subtitle")}
        </p>

        <ul className="mt-[1.6cqw] flex flex-col gap-[0.85cqw]">
          <AnimatePresence initial={false}>
            {items.slice(0, step + 1).map((item, index) => {
              const isDone = index < step;
              return (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-[0.9cqw] text-[1.2cqw]"
                >
                  <span
                    className={cn(
                      "grid size-[1.6cqw] shrink-0 place-items-center rounded-[0.35cqw] border transition-colors duration-300",
                      isDone
                        ? "border-[#2383e2] bg-[#2383e2] text-white"
                        : "border-black/25"
                    )}
                  >
                    {isDone && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          type: "spring",
                          stiffness: 500,
                          damping: 18,
                        }}
                      >
                        <Check className="size-[1.2cqw]" strokeWidth={3.5} />
                      </motion.span>
                    )}
                  </span>
                  <span
                    className={cn(
                      "transition-colors duration-500",
                      isDone && "text-black/45 line-through decoration-black/25"
                    )}
                  >
                    {index === step ? (
                      <Typewriter text={item} speed={TYPING_SPEED} caret />
                    ) : (
                      item
                    )}
                  </span>
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      </div>
    </AppWindow>
  );
}

// =============================================================================
